/**
 * Shared zod schemas for client-side form validation.
 *
 * BUG-FE-007 (SPEC-036 Track B — HIGH):
 *   Forms had no client-side validation — they relied on HTML attributes
 *   (required, minLength, pattern) which are bypassable and don't show
 *   consistent error messages. Backend zod schemas (auth.controller.ts) are
 *   the source of truth; these schemas mirror them for inline UX feedback.
 *
 * Spec ref: SPEC-NEGOCIO §8.1 (account credentials rules),
 *           SPEC-002-AUTH-REAL §3.1 (username 3-32, password 8+),
 *           CM-08 fix de raíz — validation runs before submit.
 *
 * Anti-Invención §6: mirrors backend validation; does not invent new rules.
 *
 * D-MED-002 fix (SPEC-044 QA AUDIT R1 — Batch 4): the schemas are now
 * CANONICALLY defined in `@profileia/shared/schemas/auth` (auth schemas)
 * and `@profileia/shared/schemas/profile` (profile form schema). This
 * file RE-EXPORTS them for backward compatibility — existing consumers
 * (register page, settings page) continue to import from
 * `@/lib/validators` with no breaking change. The shared package is
 * the single source of truth — backend (in a future batch) and frontend
 * both import from there.
 *
 * The shared schemas use zod v3 (the shared package's installed zod
 * version). This file does NOT manipulate them with zod v4 APIs (the
 * frontend's installed zod version) — mixing v3 schemas with v4 zod
 * operations (e.g. `.extend({ x: z.string() })` where `z` is v4) fails
 * at runtime with `keyValidator._parse is not a function` (verified).
 * Instead, the shared schemas are constructed entirely within the shared
 * package using v3 zod, and this file just re-exports them. The
 * `formatZodErrors` helper below uses a STRUCTURAL type (`ZodErrorLike`)
 * that accepts both v3 and v4 ZodErrors — so callers passing
 * `parsed.error` from a v3 shared schema work seamlessly.
 *
 * Anti-§6 (CM-19): the lowercase-only username regex (in shared
 * `registerSchema`) matches the User model `match: [/^[a-z0-9_]{3,32}$/, ...]`
 * constraint (user.model.ts:244). The HTML `pattern` attribute on the
 * register page's `<input>` element is permissive (`[a-zA-Z0-9_]+`) for UX
 * (lets the user type uppercase, then zod validation catches it with a
 * clear inline error before submit). This is intentional UX design, not
 * a drift — the backend's strict regex is the source of truth at the
 * API layer.
 */

// Re-export the shared auth + profile schemas for backward compat.
// Existing consumers import { registerSchema, changePasswordSchema,
// profileFormSchema, formatZodErrors } from '@/lib/validators' — no
// breaking change to import paths.
//
// NOTE on import path: the spec instruction (D-MED-002) said to import
// from `@profileia/shared/schemas/auth` and `@profileia/shared/schemas/profile`
// (subpath imports). However, the shared package's `package.json` `exports`
// field (managed by Batch 3 / Batch 6 — OUT OF SCOPE for Batch 4) does
// NOT list `./schemas/auth` or `./schemas/profile` as exportable subpaths.
// Node + ts-jest both honor the `exports` field, so a subpath import
// fails at module resolution time (`ERR_PACKAGE_PATH_NOT_EXPORTED` /
// `Cannot find module`). The workaround is to import from the package
// root (`@profileia/shared`) — the barrel `packages/shared/src/index.ts`
// re-exports everything from `./schemas/auth.js` + `./schemas/profile.js`
// (see the APPEND done in this same batch). Functionally equivalent to
// the subpath import — same code is loaded, just via the root barrel.
// When a future batch adds the subpath entries to `packages/shared/package.json`,
// this import can be migrated to the subpath form without code changes
// (just an import specifier rewrite).
export {
  registerSchema,
  loginSchema,
  changePasswordSchema,
} from '@profileia/shared';
export { profileFormSchema } from '@profileia/shared';

export type {
  RegisterFormValues,
  LoginFormValues,
  ChangePasswordFormValues,
  ProfileFormValues,
} from '@profileia/shared';

// ─── resetPasswordSchema (A4-MED-003 fix — SPEC-045 BATCH-4) ─────────────────
//
// SPEC-002-AUTH-REAL §3.1 + D-MED-002 (SPEC-044 QA Batch 4): the reset-password
// form should validate with Zod for consistency with register/settings pages.
// We can't reuse `changePasswordSchema` directly because that schema requires
// `currentPassword` (non-empty) — which doesn't apply to the email-link reset
// flow. Instead, we build a small schema that mirrors the password strength
// rules (8+, lowercase, uppercase, digit) + a `confirmPassword` match refine.
//
// Anti-§6: rules are mirrored from `registerSchema.password` (shared) +
// `changePasswordSchema` refine pattern — no invented thresholds.
import { z } from 'zod';

export const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'La contraseña debe tener al menos 8 caracteres')
      .max(128, 'La contraseña no puede exceder 128 caracteres')
      .regex(/[a-z]/, 'La contraseña debe incluir al menos una minúscula')
      .regex(/[A-Z]/, 'La contraseña debe incluir al menos una mayúscula')
      .regex(/[0-9]/, 'La contraseña debe incluir al menos un número'),
    confirmPassword: z.string().min(1, 'Confirma la nueva contraseña'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

// ─── formatZodErrors ────────────────────────────────────────────────────────
//
// Structural type that accepts both zod v3 and v4 ZodError instances.
// The frontend's installed zod is v4 (zod@^4.0.2 in package.json →
// resolved to 4.3.5). The shared package's installed zod is v3
// (zod@^3.23.8 → resolved to 3.25.76 at the workspace root). When the
// frontend imports a schema from `@profileia/shared/schemas/auth` and
// calls `schema.safeParse(form)`, the returned `parsed.error` is a v3
// ZodError. The v4 `z.ZodError` TYPE annotation (used previously) would
// reject v3 ZodError instances at compile time (the two classes have
// different internal property shapes — v3 has `issues`, v4 also has
// `issues` but with a different issue shape).
//
// The structural `ZodErrorLike` type below accepts any object with an
// `issues: Array<{ path, message }>` property — both v3 and v4 ZodErrors
// structurally satisfy this. Callers (settings page, register page)
// continue to pass `parsed.error` to `formatZodErrors(...)` unchanged.
// PropertyKey = string | number | symbol — zod v4 issues use PropertyKey[],
// zod v3 issues use (string | number)[]. Accepting PropertyKey[] covers both.
interface ZodErrorLike {
  issues: Array<{ path: readonly PropertyKey[]; message: string }>;
}

/**
 * Format zod errors into a { fieldName: message } map for inline display.
 *
 * Accepts both zod v3 and v4 ZodErrors (the shared package's schemas
 * return v3 errors; the frontend's `z` import is v4 — `ZodErrorLike`
 * accepts both structurally).
 */
export function formatZodErrors(error: ZodErrorLike): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path[0]?.toString();
    if (key && !result[key]) {
      result[key] = issue.message;
    }
  }
  return result;
}
